import {FetchPolicy, gql} from '@apollo/client';
import {css, StyleSheet} from 'aphrodite';
import React, {useEffect, useState} from 'react';

import BenchlingConfig from 'BenchlingConfig';
import {ProcedureType, PublishingStatus} from 'graphql-client/__generated/graphqlGlobalTypes';
import {QueryResultType, useBenchlingQuery} from 'graphql-client/BenchlingQuery';
import {getFragmentWrapper} from 'graphql-client/declarative-translations/external/getFragmentWrapper';
import {isInaccessible} from 'graphql-client/InaccessibleInfo';
import navigateToURL from 'lib/navigateToURL';
import TemplateCollectionDAO from 'notebook/entry-templates/collections/TemplateCollectionDAO';
import {getProcedureIcon} from 'procedures/design/procedureIconUtils';
import {procedurePageUrl} from 'procedures/network/procedureUrls';
import getProcedureUIString, {ProceduresStringKeys, StringCase} from 'procedures/ProceduresStringKeys';
import useValidateProcedureById, {
  ProcedureValidationErrorType,
  ValidateProcedureResult,
} from 'procedures/shared-components/validation/useValidateProcedureById';
import {getProcedureRunUIStringForStudyType, getStudyTypeForProcedureType} from 'studies/TypedStudyUtils';
import InlineBanner, {InlineBannerType} from 'ui-library/banners/InlineBanner';
import * as Colors from 'ui-library/Colors';
import SelectDropdown from 'ui-library/dropdowns/SelectDropdown';
import BenchlingDuotoneIcon from 'ui-library/icons/BenchlingDuotoneIcon';
import Link from 'ui-library/Link';
import Menu from 'ui-library/menus/Menu';
import {OptionData} from 'ui-library/menus/MenuConstants';
import SearchableMenu from 'ui-library/menus/SearchableMenu';
import {CenteredSpinner} from 'ui-library/Spinner';
import {assertNever} from 'util/Types';

import {
  ProceduresDropdownListingQuery_proceduresListing_ProcedureNode as ProcedureDropdownTemplate,
  ProceduresDropdownListingQuery,
} from './__generated/ProceduresDropdownListingQuery';

export const PROCEDURES_DROPDOWN_LISTING_QUERY = gql`
  query ProceduresDropdownListingQuery($procedureTypes: [ProcedureType!])
  @skipAudit
  @engComponent(name: STUDIES) {
    proceduresListing(procedureTypes: $procedureTypes, excludeArchived: true) {
      id
      name
      editUrl
      publishedProcedureFlowchartConfigVersionProxy {
        id
      }
      templateCollection {
        id
        name
      }
      currentPublishingRecord {
        ... on PublishingRecordNode {
          id
          publishingStatus
        }
        ...InaccessibleInfoGraphQL
      }
    }
  }
  ${getFragmentWrapper('InaccessibleInfo').fragment}
`;

interface ProcedureDropdownProps {
  isDisabled: boolean;
  procedureType: ProcedureType;
  selectedProcedureId: string | null;
  onChangeSelectedProcedure: (procedureId: string | null) => void;
  onRequestCloseModal: () => void;
  placeholderText?: string;
  procedureFetchPolicy: FetchPolicy;
  /** If provided, excludes this procedure from the dropdown options */
  /** most often used to exclude the current procedure from the dropdown options */
  excludeProcedureId?: string;
  /** If true, hides legacy procedures and only shows PoW-backed procedures */
  excludeLegacyProcedures?: boolean;
}

type ProcedureDropdownQueryData =
  | {status: QueryResultType.PENDING}
  | {status: QueryResultType.ERROR}
  | {status: QueryResultType.SUCCESS; procedures: Array<ProcedureDropdownTemplate>};

interface ProcedureDropdownProceduresProp {
  procedures: Array<ProcedureDropdownTemplate>;
}

/**
 * @package exported for testing
 */
export function getProcedureDisplayText(template: ProcedureDropdownTemplate): string {
  return `${template.templateCollection.name} / ${template.name}`;
}

function makeOptionData(procedure: ProcedureDropdownTemplate, procedureType: ProcedureType): OptionData {
  return {
    name: getProcedureDisplayText(procedure),
    value: procedure.id,
    icon: getProcedureIcon(procedureType),
    className: 'newDropdown-schemaOption',
    extra: {unstarrable: false},
  };
}

type PresentationalProcedureDropdownProps = ProcedureDropdownProps & ProcedureDropdownProceduresProp;
interface UseProcedureValidatorProps {
  procedureType: ProcedureType;
  selectedProcedureId: string | null;
  onChangeToValidatedProcedure: (procedureId: string | null) => void;
  onRequestCloseModal: () => void;
  stagedProcedureForValidationId?: string;
}

export function useProcedureValidator(props: UseProcedureValidatorProps): JSX.Element | null {
  const procedureValidationResult: ValidateProcedureResult = useValidateProcedureById(
    props.stagedProcedureForValidationId
  );
  const {selectedProcedureId, stagedProcedureForValidationId, onChangeToValidatedProcedure} = props;
  useEffect(() => {
    if (
      stagedProcedureForValidationId != null &&
      stagedProcedureForValidationId !== selectedProcedureId &&
      procedureValidationResult != null &&
      procedureValidationResult.isValid
    ) {
      onChangeToValidatedProcedure(stagedProcedureForValidationId);
    }
  }, [
    procedureValidationResult,
    stagedProcedureForValidationId,
    selectedProcedureId,
    onChangeToValidatedProcedure,
  ]);

  return (
    <>
      {procedureValidationResult?.isValid === false &&
        (procedureValidationResult.errorType === ProcedureValidationErrorType.NOT_LOADED ? (
          <ProcedureValidationLoadingSpinner procedureType={props.procedureType} />
        ) : (
          <PresentationalProcedureValidationWarning
            procedureType={props.procedureType}
            editUrl={
              props.stagedProcedureForValidationId != null
                ? procedurePageUrl(props.stagedProcedureForValidationId)
                : undefined
            }
            onRequestCloseModal={props.onRequestCloseModal}
          />
        ))}
    </>
  );
}

export function PresentationalProcedureDropdown(props: PresentationalProcedureDropdownProps): JSX.Element {
  const filteredProcedures = props.excludeProcedureId
    ? props.procedures.filter((p) => p.id !== props.excludeProcedureId)
    : props.procedures;
  const optionDatas = filteredProcedures.map((procedure) => makeOptionData(procedure, props.procedureType));
  const [stagedProcedureForValidation, setStagedProcedureForValidation] =
    useState<ProcedureDropdownTemplate | null>(
      props.selectedProcedureId
        ? (props.procedures.find((p) => p.id === props.selectedProcedureId) ?? null)
        : null
    );
  const procedureValidationResultElement = useProcedureValidator({
    procedureType: props.procedureType,
    selectedProcedureId: props.selectedProcedureId,
    onChangeToValidatedProcedure: props.onChangeSelectedProcedure,
    onRequestCloseModal: props.onRequestCloseModal,
    stagedProcedureForValidationId: stagedProcedureForValidation?.id,
  });

  return (
    <>
      <SelectDropdown
        disabled={props.isDisabled}
        displayName={
          stagedProcedureForValidation
            ? getProcedureDisplayText(stagedProcedureForValidation)
            : (props.placeholderText ?? '')
        }
        shouldEllipsize={true}
        dataTestAttrs={{component: 'PresentationalProcedureDropdown', element: 'root'}}
        menu={
          <SearchableMenu
            className="menu--short"
            scrollable={true}
            width={Menu.WIDTHS.FULL}
            onSelect={(procedureId: string) => {
              // sets the selected procedure on the modal as null to disable next button until validation
              // succeeds
              props.onChangeSelectedProcedure(null);
              const procedure = props.procedures.find((p) => p.id === procedureId)!;
              setStagedProcedureForValidation(procedure);
            }}
            optionDatas={optionDatas}
            selectedValue={stagedProcedureForValidation?.id}
          />
        }
      />
      {procedureValidationResultElement}
    </>
  );
}

export function PresentationalProcedureNoAvailableProceduresWarningBanner(
  props: ProcedureDropdownProps
): JSX.Element {
  const studyType = getStudyTypeForProcedureType(props.procedureType);
  const procedureRunUIString = getProcedureRunUIStringForStudyType(studyType);
  const procedureString = getProcedureUIString(ProceduresStringKeys.PROCEDURE, props.procedureType, {
    stringCase: StringCase.LOWER,
    prependAorAn: true,
  });
  const preLinkBannerMessage = `To begin creating your ${procedureRunUIString} plan, please configure ${procedureString} by clicking `;
  const postLinkBannerMessage = ' or contact your Benchling administrator for assistance.';

  return (
    <div className={css(styles.banner)}>
      <InlineBanner
        bannerType={InlineBannerType.WARNING}
        supportingInfo={
          <p className={css(styles.message)}>
            <strong>
              {preLinkBannerMessage}
              <Link
                onClick={() => {
                  navigateToURL(TemplateCollectionDAO.collectionsURL());
                  props.onRequestCloseModal();
                }}
              >
                here
              </Link>
              {postLinkBannerMessage}
            </strong>
          </p>
        }
      />
    </div>
  );
}

export function ProcedureValidationLoadingSpinner({
  procedureType,
}: {
  procedureType: ProcedureType;
}): JSX.Element {
  return (
    <div className={css(styles.loadingValidationStateText)}>
      <BenchlingDuotoneIcon
        icon="spinner-third"
        primaryColor={Colors.BLUE_LIGHT}
        secondaryColor={Colors.BLUE_POWDER_LIGHT}
        spin={true}
      />
      {` Checking ${getProcedureUIString(ProceduresStringKeys.PROCEDURE, procedureType, {
        stringCase: StringCase.LOWER,
      })} for errors...`}
    </div>
  );
}

export function PresentationalProcedureValidationWarning(props: {
  editUrl?: string;
  procedureType: ProcedureType;
  onRequestCloseModal: () => void;
}): JSX.Element {
  const containsErrorsText = `${getProcedureUIString(ProceduresStringKeys.PROCEDURE, props.procedureType, {
    stringCase: StringCase.SENTENCE,
  })} contains errors. Fix errors to run the ${getProcedureUIString(
    ProceduresStringKeys.PROCEDURE,
    props.procedureType,
    {
      stringCase: StringCase.LOWER,
    }
  )}. `;
  const goToProcedureRunText = `Go to ${getProcedureUIString(
    ProceduresStringKeys.PROCEDURE,
    props.procedureType,
    {
      stringCase: StringCase.LOWER,
    }
  )}`;
  return (
    <div className={css(styles.banner)}>
      <InlineBanner
        bannerType={InlineBannerType.ERROR}
        supportingInfo={
          <p>
            <strong>
              {containsErrorsText}
              {props.editUrl && (
                <Link
                  onClick={() => {
                    if (props.editUrl) {
                      navigateToURL(props.editUrl);
                    }
                    props.onRequestCloseModal();
                  }}
                >
                  {goToProcedureRunText}
                </Link>
              )}{' '}
            </strong>
          </p>
        }
      />
    </div>
  );
}

export function shouldShowProcedureForProcedureRunCreation(
  procedure: Pick<ProcedureDropdownTemplate, 'currentPublishingRecord'>
): boolean {
  if (!BenchlingConfig.PROCEDURES_ENABLE_PUBLISHING) {
    return true;
  }

  const currentPublishingRecord = procedure.currentPublishingRecord;
  if (currentPublishingRecord && !isInaccessible(currentPublishingRecord)) {
    return currentPublishingRecord.publishingStatus === PublishingStatus.EFFECTIVE;
  }
  return false;
}

function useProcedureDropdownQueryData(
  procedureTypeFilter: ProcedureType,
  procedureFetchPolicy: FetchPolicy = 'cache-first',
  excludeLegacyProcedures: boolean = false
): ProcedureDropdownQueryData {
  const procedureDropdownResult = useBenchlingQuery<ProceduresDropdownListingQuery>(
    PROCEDURES_DROPDOWN_LISTING_QUERY,
    {
      renderCachedDataWhenRefetching: true,
      fetchPolicy: procedureFetchPolicy,
      variables: {
        procedureTypes: [procedureTypeFilter],
      },
    }
  );
  if (procedureDropdownResult.type === QueryResultType.PENDING) {
    return {status: QueryResultType.PENDING};
  }
  if (procedureDropdownResult.type === QueryResultType.ERROR) {
    return {status: QueryResultType.ERROR};
  }

  return {
    status: QueryResultType.SUCCESS,
    procedures: procedureDropdownResult.data.proceduresListing
      .filter(
        (procedure) =>
          shouldShowProcedureForProcedureRunCreation(procedure) &&
          (!excludeLegacyProcedures || procedure.publishedProcedureFlowchartConfigVersionProxy != null)
      )
      .toSorted(
        (a, b) =>
          // Sort by template name, then procedure name
          a.templateCollection.name.localeCompare(b.templateCollection.name) || a.name.localeCompare(b.name)
      ),
  };
}

export default function ProcedureDropdown(props: ProcedureDropdownProps): JSX.Element | null {
  const procedureDropdownQueryData = useProcedureDropdownQueryData(
    props.procedureType,
    props.procedureFetchPolicy,
    props.excludeLegacyProcedures
  );
  switch (procedureDropdownQueryData.status) {
    case QueryResultType.PENDING:
      return <CenteredSpinner />;
    case QueryResultType.ERROR:
      return null;
    case QueryResultType.SUCCESS:
      return procedureDropdownQueryData.procedures.length === 0 ? (
        <PresentationalProcedureNoAvailableProceduresWarningBanner {...props} />
      ) : (
        <PresentationalProcedureDropdown {...props} procedures={procedureDropdownQueryData.procedures} />
      );
    default:
      return assertNever(procedureDropdownQueryData);
  }
}

const styles = StyleSheet.create({
  banner: {
    marginTop: 10,
  },
  message: {
    marginBottom: 0,
  },
  loadingValidationStateText: {
    fontStyle: 'italic',
    marginTop: 10,
  },
});
